import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Voice input for the CUI chat composer. Records mic audio in the browser and
 * sends it to /api/speech/transcribe (Whisper via AI Bridge — the same backend
 * werking-report uses). The recognised text is handed back via onTranscript so
 * the caller can append it to the chat input.
 *
 * Renders nothing when the Whisper backend is unavailable (no Bridge key), so
 * the composer degrades cleanly instead of showing a dead button.
 */
export function VoiceMicButton({
  onTranscript,
  language = 'de',
  disabled,
}: {
  onTranscript: (text: string) => void;
  language?: string;
  disabled?: boolean;
}) {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Probe the Whisper backend once on mount.
  useEffect(() => {
    let alive = true;
    fetch('/api/speech/transcribe')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setAvailable(!!d && d.status === 'ready'); })
      .catch(() => { if (alive) setAvailable(false); });
    return () => { alive = false; };
  }, []);

  const stop = useCallback(() => {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    setRecording(false);
  }, []);

  const start = useCallback(async () => {
    // Chromium only exposes mediaDevices in secure contexts (https/localhost).
    // Over plain http the property is undefined and recording can never work —
    // say so instead of a generic failure.
    if (!navigator.mediaDevices?.getUserMedia) {
      alert(
        'Mikrofon nicht verfügbar: Die CUI läuft über eine unsichere Verbindung (http). '
        + 'Spracheingabe braucht https oder eine aktualisierte Desktop-App.',
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];

      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };

      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size === 0) { setProcessing(false); return; }
        setProcessing(true);
        try {
          const fd = new FormData();
          const ext = mime.split('/')[1] || 'webm';
          fd.append('audio', blob, `recording.${ext}`);
          fd.append('language', language);
          const r = await fetch('/api/speech/transcribe', { method: 'POST', body: fd });
          const d: { success?: boolean; text?: string; error?: string } = await r.json();
          if (d.success && d.text) onTranscript(d.text.trim());
          else if (d.error) alert(`Transkription fehlgeschlagen: ${d.error}`);
        } catch {
          alert('Transkription fehlgeschlagen. Bitte erneut versuchen.');
        } finally {
          setProcessing(false);
        }
      };

      recRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotAllowedError') {
        alert('Mikrofonzugriff wurde verweigert. Bitte im Browser erlauben.');
      } else {
        console.error('[VoiceMicButton] Aufnahme-Start fehlgeschlagen:', e);
        alert(`Aufnahme konnte nicht gestartet werden${e instanceof Error ? ` (${e.name}: ${e.message})` : ''}.`);
      }
    }
  }, [language, onTranscript]);

  // Cleanup on unmount.
  useEffect(() => () => {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
  }, []);

  // Hide entirely when the backend can't transcribe.
  if (available === false) return null;

  const toggle = () => { if (processing) return; if (recording) stop(); else start(); };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled || processing || available === null}
      title={
        processing ? 'Wird transkribiert…'
          : recording ? 'Aufnahme stoppen'
            : 'Spracheingabe (Whisper)'
      }
      style={{
        width: 36, minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: recording ? 'rgba(239,68,68,0.18)' : processing ? 'rgba(59,130,246,0.15)' : 'var(--tn-bg)',
        border: `1px solid ${recording ? 'var(--tnf-ef4444)' : processing ? 'var(--tnf-3b82f6)' : 'var(--tn-border)'}`,
        borderRadius: 4, cursor: processing ? 'default' : 'pointer', outline: 'none',
        fontSize: 16, color: recording ? 'var(--tnf-ef4444)' : 'var(--tn-text-muted)', flexShrink: 0,
      }}
    >
      {processing ? '⏳' : recording ? '⏹' : '🎤'}
    </button>
  );
}
